import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Save,
  Upload,
  Image as ImageIcon,
} from "lucide-react";

import {
  useLazyGetEmailBrandingProfileByIdQuery,
  useCreateEmailBrandingProfileMutation,
  useUpdateEmailBrandingProfileMutation,
  useUploadEmailBrandingProfileLogoMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import {
  ICreateEmailBrandingProfileRequest,
  IEmailBrandingProfile,
} from "../../../types/Request/AdminRequest";

const BrandingProfileDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);

  const [getBrandingProfile, { data: profile, isFetching }] =
    useLazyGetEmailBrandingProfileByIdQuery();

  const [createBrandingProfile, { isLoading: isCreating }] =
    useCreateEmailBrandingProfileMutation();

  const [updateBrandingProfile, { isLoading: isUpdating }] =
    useUpdateEmailBrandingProfileMutation();
  const [uploadLogo, { isLoading: isUploadingLogo }] =
    useUploadEmailBrandingProfileLogoMutation();
  const isSubmitting = isCreating || isUpdating;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await uploadLogo(formData).unwrap();

      setForm((prev) => ({
        ...prev,
        logoUrl: result.fileUrl,
      }));
    } catch (error) {
      console.error("Logo upload failed:", error);
    }
  };

  const [form, setForm] = useState<ICreateEmailBrandingProfileRequest>({
    name: "",
    companyName: "",
    tagLine: "",

    logoUrl: "",

    primaryColor: "#2E7D32",
    secondaryColor: "#FFFFFF",

    websiteUrl: "",

    supportEmail: "",
    supportPhone: "",

    address: "",

    facebookUrl: "",
    instagramUrl: "",
    twitterUrl: "",
    linkedInUrl: "",
    youTubeUrl: "",

    copyrightText: "",

    termsUrl: "",
    privacyPolicyUrl: "",

    isActive: true,
  });

  useEffect(() => {
    if (isEdit && id) {
      getBrandingProfile(Number(id));
    }
  }, [id, isEdit, getBrandingProfile]);

  useEffect(() => {
    if (!profile) return;

    setForm({
      name: profile.name ?? "",
      companyName: profile.companyName ?? "",
      tagLine: profile.tagLine ?? "",

      logoUrl: profile.logoUrl ?? "",

      primaryColor: profile.primaryColor ?? "#2E7D32",

      secondaryColor: profile.secondaryColor ?? "#FFFFFF",

      websiteUrl: profile.websiteUrl ?? "",

      supportEmail: profile.supportEmail ?? "",
      supportPhone: profile.supportPhone ?? "",

      address: profile.address ?? "",

      facebookUrl: profile.facebookUrl ?? "",
      instagramUrl: profile.instagramUrl ?? "",
      twitterUrl: profile.twitterUrl ?? "",
      linkedInUrl: profile.linkedInUrl ?? "",
      youTubeUrl: profile.youTubeUrl ?? "",

      copyrightText: profile.copyrightText ?? "",

      termsUrl: profile.termsUrl ?? "",

      privacyPolicyUrl: profile.privacyPolicyUrl ?? "",

      isActive: profile.isActive ?? true,
    });
  }, [profile]);

  const updateField = <K extends keyof ICreateEmailBrandingProfileRequest>(
    field: K,
    value: ICreateEmailBrandingProfileRequest[K],
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleBack = () => {
    navigate("/admin/adminemailtemplates/branding");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEdit && profile) {
        await updateBrandingProfile({
          id: profile.id,
          data: form,
        }).unwrap();
      } else {
        await createBrandingProfile(form).unwrap();
      }

      navigate("/admin/adminemailtemplates/branding");
    } catch (error) {
      console.error("Branding profile save failed:", error);
    }
  };

  if (isEdit && isFetching && !profile) {
    return (
      <div className="flex h-[500px] items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50/40">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
            >
              <ArrowLeft size={18} />
            </button>

            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {isEdit ? "Edit Branding Profile" : "Create Branding Profile"}
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                Configure branding used by your email layouts.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          {/* Accent */}
          <div className="h-1 w-full bg-primary" />

          <div className="p-6">
            {/* Brand Information */}
            <SectionTitle
              title="Brand Information"
              description="Basic information displayed throughout your email communications."
            />

            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
              <Input
                label="Profile Name"
                required
                value={form.name}
                onChange={(value) => updateField("name", value)}
                placeholder="Petland Default"
              />

              <Input
                label="Company Name"
                required
                value={form.companyName}
                onChange={(value) => updateField("companyName", value)}
                placeholder="Petland"
              />

              <Input
                label="Tagline"
                value={form.tagLine}
                onChange={(value) => updateField("tagLine", value)}
                placeholder="Your Pet. Our Passion."
              />
            </div>

            {/* Logo + Colors */}
            <div className="mt-8">
              <SectionTitle
                title="Visual Branding"
                description="Configure the logo and colors used in email layouts."
              />

              <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Logo */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-700">
                    Logo
                  </label>

                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5">
                    {/* Logo Preview */}
                    <div className="flex h-32 items-center justify-center rounded-lg border border-slate-200 bg-white">
                      {form.logoUrl ? (
                        <img
                          src={form.logoUrl}
                          alt="Brand logo"
                          className="max-h-24 max-w-[180px] object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="text-center text-slate-400">
                          <ImageIcon size={30} className="mx-auto" />
                          <p className="mt-2 text-xs">No logo configured</p>
                        </div>
                      )}
                    </div>

                    {/* Upload */}
                    <div className="mt-4 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-medium text-slate-700">
                          Brand Logo
                        </p>

                        <p className="mt-1 text-[11px] text-slate-400">
                          PNG, JPG or WEBP · Maximum 2 MB
                        </p>
                      </div>

                      <button
                        type="button"
                        disabled={isUploadingLogo}
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isUploadingLogo ? (
                          <>
                            <Loader2 size={15} className="animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <ImageIcon size={15} />
                            {form.logoUrl ? "Replace Logo" : "Upload Logo"}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </div>
                </div>

                {/* Colors */}
                <div className="lg:col-span-2 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <ColorInput
                    label="Primary Color"
                    required
                    value={form.primaryColor}
                    onChange={(value) => updateField("primaryColor", value)}
                  />

                  <ColorInput
                    label="Secondary Color"
                    required
                    value={form.secondaryColor}
                    onChange={(value) => updateField("secondaryColor", value)}
                  />
                </div>
              </div>
            </div>

            {/* Website / Support */}
            <div className="mt-8">
              <SectionTitle
                title="Website & Support"
                description="Contact and website information used in email footers."
              />

              <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Input
                  label="Website URL"
                  required
                  value={form.websiteUrl}
                  onChange={(value) => updateField("websiteUrl", value)}
                  placeholder="https://www.petland.com"
                />

                <Input
                  label="Support Email"
                  required
                  value={form.supportEmail}
                  onChange={(value) => updateField("supportEmail", value)}
                  placeholder="support@petland.com"
                />

                <Input
                  label="Support Phone"
                  value={form.supportPhone}
                  onChange={(value) => updateField("supportPhone", value)}
                  placeholder="+971..."
                />

                <div className="lg:col-span-3">
                  <Input
                    label="Address"
                    value={form.address}
                    onChange={(value) => updateField("address", value)}
                    placeholder="Dubai, United Arab Emirates"
                  />
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="mt-8">
              <SectionTitle
                title="Social Media"
                description="Optional social media links displayed in your email footer."
              />

              <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Input
                  label="Facebook URL"
                  value={form.facebookUrl ?? ""}
                  onChange={(value) => updateField("facebookUrl", value)}
                />

                <Input
                  label="Instagram URL"
                  value={form.instagramUrl ?? ""}
                  onChange={(value) => updateField("instagramUrl", value)}
                />

                <Input
                  label="Twitter / X URL"
                  value={form.twitterUrl ?? ""}
                  onChange={(value) => updateField("twitterUrl", value)}
                />

                <Input
                  label="LinkedIn URL"
                  value={form.linkedInUrl ?? ""}
                  onChange={(value) => updateField("linkedInUrl", value)}
                />

                <Input
                  label="YouTube URL"
                  value={form.youTubeUrl ?? ""}
                  onChange={(value) => updateField("youTubeUrl", value)}
                />
              </div>
            </div>

            {/* Legal */}
            <div className="mt-8">
              <SectionTitle
                title="Legal & Footer"
                description="Links and copyright information displayed in email footers."
              />

              <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Input
                  label="Copyright Text"
                  value={form.copyrightText}
                  onChange={(value) => updateField("copyrightText", value)}
                />

                <Input
                  label="Terms URL"
                  value={form.termsUrl}
                  onChange={(value) => updateField("termsUrl", value)}
                />

                <Input
                  label="Privacy Policy URL"
                  value={form.privacyPolicyUrl}
                  onChange={(value) => updateField("privacyPolicyUrl", value)}
                />
              </div>
            </div>

            {/* Settings */}
            <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <Toggle
                  label="Active"
                  description="Allow this profile to be used by email layouts."
                  checked={form.isActive}
                  onChange={(value) => updateField("isActive", value)}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-[#2E7D32] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#256B2A] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <Save size={17} />
              )}

              {isEdit ? "Update Profile" : "Save Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ========================================================= */
/* Reusable Components */
/* ========================================================= */

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10";

const SectionTitle: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => (
  <div>
    <h2 className="text-base font-semibold text-slate-900">{title}</h2>

    <p className="mt-1 text-xs text-slate-500">{description}</p>
  </div>
);

const Input: React.FC<{
  label: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ label, value, required, placeholder, onChange }) => (
  <div>
    <label className="mb-1.5 block text-xs font-medium text-slate-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>

    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass}
    />
  </div>
);

const ColorInput: React.FC<{
  label: string;
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, required, onChange }) => (
  <div>
    <label className="mb-1.5 block text-xs font-medium text-slate-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>

    <div className="flex gap-2">
      <input
        type="color"
        value={value || "#2E7D32"}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
      />

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        placeholder="#2E7D32"
      />
    </div>
  </div>
);

const Toggle: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <label className="flex cursor-pointer items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition ${
        checked ? "bg-[#2E7D32]" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </button>

    <span>
      <span className="block text-sm font-medium text-slate-800">{label}</span>

      <span className="block text-xs text-slate-500">{description}</span>
    </span>
  </label>
);

export default BrandingProfileDetailsScreen;
